import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import "../../styles/RightSlidePanel.css";
import axios from "axios";
import { toDateOnly } from "../../utils/dateOnly";

const TASK_IDS = {
  signed_release: 21,
};

const RightPanel = ({ user, onClose }) => {
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [workHistory, setWorkHistory] = useState([]);
  const [taskStatus, setTaskStatus] = useState({});
  const [signatures, setSignatures] = useState({});
  const [signdates, setSigndates] = useState({});
  const [expOptions, setExpOptions] = useState([]);
  const [equipOptions, setEquipOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [profileImageBroken, setProfileImageBroken] = useState(false);

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    setProfileImageBroken(false);
  }, [user?.profilePic]);

  // Check if the user is a driver based on emp_category values 2, 8, or 11
  const isDriver = [2, 8, 11].includes(Number(user.emp_category));

  useEffect(() => {
    if (!user?.id) {
      setLicenseInfo(null);
      return;
    }

    setLicenseInfo(null); // Clear previous data immediately

    const fetchLicense = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/driverlicense/${user.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setLicenseInfo(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch driver license:", err);
        setLicenseInfo(null);
      }
    };

    fetchLicense();
  }, [user?.id, BASE_URL]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchWorkHistory = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/work-history/${user.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setWorkHistory(res.data);
      } catch (err) {
        console.error("❌ Failed to fetch work history:", err);
      }
    };

    fetchWorkHistory();
  }, [user?.id, BASE_URL]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchUserTasks = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/usertasks/status/${user.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        setTaskStatus(res.data.status || {});
        setSignatures(res.data.signatures || {});
        setSigndates(res.data.signdates || {});
      } catch (err) {
        console.error("❌ Failed to fetch user task status:", err);
      }
    };

    fetchUserTasks();
  }, [user?.id, BASE_URL]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const config = { headers: { Authorization: `Bearer ${token}` } };

    axios
      .all([
        axios.get(`${BASE_URL}/api/dropdowns/exp_list`, config),
        axios.get(`${BASE_URL}/api/dropdowns/equip-operated`, config),
        axios.get(`${BASE_URL}/api/dropdowns/reg-travelled`, config),
      ])
      .then(
        axios.spread((expRes, equipRes, regionRes) => {
          setExpOptions(expRes.data);
          setEquipOptions(equipRes.data);
          setRegionOptions(regionRes.data);
        }),
      )
      .catch((err) => {
        console.error("Failed to load dropdown mappings:", err);
      });
  }, [user?.id, BASE_URL]);

  const getExpLabel = (id) => {
    const exp = expOptions.find((e) => Number(e.el_id) === Number(id));
    if (!exp) {
      console.warn("Missing experience label for ID:", id);
    }
    return exp?.el_name || "";
  };

  const getEquipLabel = (id) => {
    const eq = equipOptions.find((e) => e.eo_id === Number(id));
    return eq?.eo_name || `Equipment ID ${id}`;
  };

  const getRegionLabel = (id) => {
    const reg = regionOptions.find((r) => r.rt_id === Number(id));
    return reg?.rt_name || `Region ID ${id}`;
  };

  const signedReleaseDate =
    signdates?.[String(TASK_IDS.signed_release)] ?? signdates?.signed_release;

  if (!user) return null;
  const userInitials = [user.first_name, user.last_name]
    .filter(Boolean)
    .map((name) => name.trim().charAt(0).toUpperCase())
    .join("") || "U";
  const profileImageUrl = user.profilePic
    ? `${BASE_URL}/uploads/profilepic/${user.profilePic}`
    : null;

  return (
    <div
      className="right-slide-panel"
      onMouseLeave={() => {
        if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
          onClose();
        }
      }}
    >
      <button
        type="button"
        className="right-slide-panel-close"
        aria-label="Close profile preview"
        onClick={onClose}
      >
        &times;
      </button>
      <div className="panel-container">
        <div className="candidate-profile-summary">
          <div className={`candidate-profile-avatar ${profileImageUrl && !profileImageBroken ? "has-image" : ""}`}>
            {profileImageUrl && !profileImageBroken ? (
              <img
                src={profileImageUrl}
                alt={`${user.first_name || "User"} ${user.last_name || ""}`.trim()}
                onError={() => setProfileImageBroken(true)}
              />
            ) : (
              <span aria-hidden="true">{userInitials}</span>
            )}
          </div>
          <div className="candidate-profile-identity">
            <span className="candidate-profile-eyebrow">Applicant profile</span>
            <h2 className="user-name">
              {user.first_name} {user.last_name}
              <i className="fas fa-edit candidate-profile-edit" aria-hidden="true" />
            </h2>
          <div className="user-header-left">
            <p><i className="fas fa-envelope" aria-hidden="true" />{user.email || "Not provided"}</p>
            <p><i className="fas fa-phone-alt" aria-hidden="true" />{user.mobile || "Not provided"}</p>
            <div className="address-block">
              <i className="fas fa-map-marker-alt" aria-hidden="true" />
              <span>{[user.address1, user.city, user.province, user.postal_code, "Canada"].filter(Boolean).join(", ") || "Address not provided"}</span>
            </div>
          </div>
          </div>
          <div className="user-header-right">
            <span>Birth date</span>
            <strong>{toDateOnly(user.dob) || "Not provided"}</strong>
          </div>
        </div>

        <div className="main-columns">
          {/* Left Column */}
          <div className="column">
            <Section title="Personal Information">
              <KeyValue
                label="Did someone refer you?"
                value={
                  user.refer_status === "Yes"
                    ? (() => {
                        try {
                          const refObj =
                            typeof user.refer_by === "string"
                              ? JSON.parse(user.refer_by)
                              : user.refer_by;

                          if (refObj && typeof refObj === "object") {
                            let display = refObj.name || "";
                            if (refObj.employeeId) {
                              display += ` (${refObj.employeeId})`;
                            }
                            return `Yes${display ? `, ${display}` : ""}`;
                          }
                        } catch {
                          // fallback for legacy plain-text values
                          if (user.refer_by) {
                            return `Yes, ${user.refer_by}`;
                          }
                        }
                        return "Yes";
                      })()
                    : "No"
                }
              />

              {isDriver && (
                <>
                  <KeyValue
                    label="Legal right to work in the U.S.?"
                    value={user.dq_legal_right_work_us || "-"}
                  />
                  <KeyValue
                    label="Legal right to enter Canada?"
                    value={user.dq_legal_right_enter_canada || "-"}
                  />
                  <KeyValue
                    label="Worked for New Way Transport before?"
                    value={user.dq_previous_nwt_employee || "-"}
                  />
                </>
              )}
            </Section>
            {isDriver && (
              <Section title="Driver's Licence">
                <KeyValue
                  label="Driver's Licence"
                  value={licenseInfo?.dl_license_no || "-"}
                />
                <KeyValue label="Class" value={licenseInfo?.dl_class || "-"} />
                <KeyValue
                  label="Province"
                  value={licenseInfo?.dl_province || "-"}
                />
                <KeyValue
                  label="Country"
                  value={licenseInfo?.dl_country || "-"}
                />
                <KeyValue
                  label="Exp. Date"
                  value={
                    licenseInfo?.dl_expdate
                      ? new Date(licenseInfo.dl_expdate)
                          .toISOString()
                          .split("T")[0]
                      : "-"
                  }
                />
              </Section>
            )}
            <Section title="Additional Questions">
              <KeyValue
                label="Do you have any physical condition which may limit your ability to perform the job applied for?"
                value={user.dq_physical_condition || "-"}
              />
            </Section>
            <Section title="Final Qualification Questions">
              <KeyValue
                label="Highest Level of Education"
                value={user.dq_education || "-"}
              />
              <KeyValue
                label="Last School Attended"
                value={user.dq_school || "-"}
              />

              {!isDriver ? (
                <>
                  {Object.entries(user.dq_exp_detail || {}).map(
                    ([expId, { years, desc }], index, arr) => {
                      const label = getExpLabel(expId) || `ID ${expId}`;
                      const valueParts = [label, years, desc]
                        .filter(Boolean)
                        .join(" - ");

                      return (
                        <div key={expId}>
                          <KeyValue
                            label={index === 0 ? "Experience" : ""}
                            value={valueParts}
                          />
                        </div>
                      );
                    },
                  )}
                </>
              ) : (
                <>
                  <KeyValue
                    label="Regions Travelled"
                    value={
                      Array.isArray(user.dq_reg_travelled)
                        ? user.dq_reg_travelled
                            .map((id) => getRegionLabel(id))
                            .join(", ")
                        : "-"
                    }
                  />
                  {Object.entries(user.dq_equip_operated_detail || {}).map(
                    ([eodId, { miles, desc }], index, arr) => {
                      const label = getEquipLabel(eodId) || `ID ${eodId}`;
                      const valueParts = [label, miles, desc]
                        .filter(Boolean)
                        .join(" - ");

                      return (
                        <div key={eodId}>
                          <KeyValue
                            label={index === 0 ? "Equipment Operated" : ""}
                            value={valueParts}
                          />
                        </div>
                      );
                    },
                  )}
                </>
              )}
            </Section>

            {isDriver && (
              <>
                <Section title="Qualification Information">
                  <KeyValue
                    label="Have you ever been convicted of a crime for which a pardon has not been granted?"
                    value={user.dq_conviction || "-"}
                  />
                  <KeyValue
                    label="FAST Card?"
                    value={user.dq_fast_card || "-"}
                  />
                  <KeyValue
                    label="Have you ever had your FAST Card Revoked or Denied?"
                    value={user.dq_fast_card_revoked || "-"}
                  />
                </Section>

                <Section title="Accidents / Incidents?">
                  <KeyValue
                    label="Accidents/Incidents over the last 5 years (even if not at fault)"
                    value={user.dq_accidents_last5yr || "-"}
                  />
                </Section>

                <Section title="Moving Violations / Traffic Convictions?">
                  <KeyValue
                    label="Moving Violations in the last 3 years"
                    value={user.dq_violations_last3yr || "-"}
                  />
                </Section>

                <Section title="Courses">
                  <KeyValue
                    label="Do you have any training courses related to this position?"
                    value={user.dq_training || "-"}
                  />
                </Section>

                <Section title="Awards">
                  <KeyValue
                    label="Do you have any safe driving awards?"
                    value={user.dq_safe_driving_awards || "-"}
                  />
                </Section>

                <Section title="Driver Licence Questions">
                  <KeyValue
                    label="When did you obtain your Commercial Driver's Licence or plan to do so?"
                    value={
                      user.dq_commercial_license_date
                        ? dayjs(user.dq_commercial_license_date).format(
                            "MM/DD/YYYY",
                          )
                        : "-"
                    }
                  />
                  <KeyValue
                    label="Have you ever been denied a licence, permit or privilege to operate a motor vehicle?"
                    value={user.dq_license_denied || "-"}
                  />
                  <KeyValue
                    label="Do you have a clean abstract and driving record?"
                    value={user.dq_abstract_clean || "-"}
                  />

                  {user.dq_abstract_explanation && (
                    <KeyValue
                      label="Explanation:"
                      value={user.dq_abstract_explanation}
                    />
                  )}
                  <KeyValue
                    label="Has any licence, permit or privilege ever been suspended or revoked?"
                    value={user.dq_license_suspended || "-"}
                  />
                </Section>
              </>
            )}
            <Section title="Signed Release">
              <p className="signed-release-section">
                I certify that this application was completed by me and that all
                entries on it and information in it are true and complete to the
                best of my knowledge. I understand that any misstatement or
                omission of information in this application may result in my
                dismissal.
              </p>

              {taskStatus?.signed_release === "completed" && (
                <div className="border rounded p-4 shadow-sm bg-white">
                  <img
                    src={signatures?.signed_release}
                    alt="Signature"
                    className="max-w-[250px] mb-2"
                  />

                  <div className="border-t border-dashed border-gray-400 w-[250px] mb-1" />

                  <div className="signed-release-section">
                    {user.first_name} {user.last_name}
                  </div>

                  <div className="signed-release-section">
                    {signedReleaseDate
                      ? dayjs(signedReleaseDate).format("MM/DD/YYYY")
                      : "-"}
                  </div>
                </div>
              )}
            </Section>
          </div>

          {/* Right Column */}
          <div className="column">
            <Section title="Employment/Contracted To History">
              {workHistory.map((rec, index) => (
                <div key={rec.wh_id} className="employment-entry">
                  {/* Top Row */}
                  <div className="employment-header">
                    <div className="employment-type">
                      {rec.wh_type === "Working" && <><i className="fas fa-briefcase me-1" aria-hidden="true" />Working</>}
                      {rec.wh_type === "Unemployed" && "⭕ Unemployed"}
                      {rec.wh_type === "School" && <><i className="fas fa-graduation-cap me-1" aria-hidden="true" />School</>}
                      {rec.wh_type === "Driving School" && <><i className="fas fa-truck me-1" aria-hidden="true" />Driving School</>}
                      {rec.wh_type === "Military" && <><i className="fas fa-shield-alt me-1" aria-hidden="true" />Military</>}
                    </div>
                    <div className="employment-dates">
                      {dayjs(rec.wh_datefrom).format("MM/YYYY")} →{" "}
                      {rec.wh_dateto
                        ? dayjs(rec.wh_dateto).format("MM/YYYY")
                        : "Present"}
                    </div>
                  </div>

                  {/* Main Content */}
                  {rec.wh_type === "Working" && (
                    <>
                      <div className="employment-org">
                        <strong>{rec.wh_name}</strong>
                        <div>
                          {rec.wh_address1},{rec.wh_address2}, {rec.wh_city},{" "}
                          {rec.wh_province}
                        </div>
                        <div>
                          {rec.wh_country} {rec.wh_postcode}
                        </div>
                      </div>

                      <div className="employment-details-grid">
                        <div>Contact Name</div>
                        <div>
                          {`${rec.wh_confname || ""} ${
                            rec.wh_conlname || ""
                          }`.trim() || "-"}
                        </div>
                        <div>Title</div>
                        <div>{rec.wh_position || "-"}</div>
                        <div>Terminated</div>
                        <div>{rec.wh_terminated || "-"}</div>
                        <div>Reason For Leaving</div>
                        <div>{rec.wh_reason || "-"}</div>
                        <div>Phone</div>
                        <div>{rec.wh_phone || "-"}</div>
                        <div>Position</div>
                        <div>{rec.wh_position || "-"}</div>
                        <div>Did you operate a commercial vehicle?</div>
                        <div>{rec.wh_comm_vehicle || "-"}</div>
                      </div>
                    </>
                  )}

                  {rec.wh_type === "Unemployed" && (
                    <div className="employment-details-grid">
                      <div>Reason for unemployment</div>
                      <div>{rec.wh_reason || "-"}</div>
                    </div>
                  )}

                  {rec.wh_type === "School" && (
                    <>
                      <div className="employment-org">
                        <strong>{rec.wh_name}</strong>
                      </div>
                      <div className="employment-details-grid">
                        <div>Level of Education</div>
                        <div>{rec.wh_position || "-"}</div>
                      </div>
                    </>
                  )}

                  {rec.wh_type === "Driving School" && (
                    <>
                      <div className="employment-org">
                        <strong>{rec.wh_name}</strong>
                        <div>{rec.wh_phone || "-"}</div>
                        <div>
                          {rec.wh_city}, {rec.wh_province}, {rec.wh_country}{" "}
                          {rec.wh_postcode}
                        </div>
                      </div>

                      <div className="employment-details-grid">
                        <div>Graduated?</div>
                        <div>{rec.wh_graduated || "-"}</div>
                        <div>GPA</div>
                        <div>{rec.wh_gpa || "-"}</div>
                        <div>Hours of Instruction</div>
                        <div>{rec.wh_hours_of_instruction || "-"}</div>
                        <div>Safety Sensitive Functions (DOT)</div>
                        <div>{rec.wh_safety_sensitive || "-"}</div>
                        <div>Transport Canada / FMCSR</div>
                        <div>{rec.wh_transport_regulation || "-"}</div>
                      </div>

                      {rec.wh_skills && rec.wh_skills.length > 0 && (
                        <div className="employment-details-grid">
                          <div>Skills Trained</div>
                          <div>
                            {Array.isArray(rec.wh_skills)
                              ? rec.wh_skills.join(", ")
                              : rec.wh_skills}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {rec.wh_type === "Military" && (
                    <>
                      <div className="employment-org">
                        <strong>Branch of Service:</strong>
                        {rec.wh_branch_service || "-"}
                      </div>
                      <div className="employment-details-grid">
                        <div>Rank</div>
                        <div>{rec.wh_military_rank || "-"}</div>
                      </div>
                    </>
                  )}

                  {/* Divider */}
                  {index < workHistory.length - 1 && (
                    <hr className="employment-divider" />
                  )}
                </div>
              ))}
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="section">
    <h4>{title}</h4>
    <div className="section-body">{children}</div>
  </div>
);

const KeyValue = ({ label, value }) => (
  <div className="key-value">
    <div className="key-value-label">{label}</div>
    <div className="key-value-value">{value}</div>
  </div>
);

export default RightPanel;
